import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Container,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Chip,
  Pagination,
  Alert,
  CircularProgress,
  Paper,
  InputAdornment,
} from '@mui/material';
import { keyframes } from '@mui/material/styles';
import {
  Search,
  Business,
  LocationOn,
  Clear,
} from '@mui/icons-material';
import { empresaService, Empresa, Categoria, Subcategoria, EmpresaFilters } from '../services/empresaService';

const floatIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(18px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const darkGridSx = {
  position: 'relative',
  overflow: 'hidden',
  backgroundColor: '#202429',
  color: '#fff',
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    opacity: 0.12,
    backgroundImage:
      'linear-gradient(rgba(255,255,255,0.9) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.9) 1px, transparent 1px)',
    backgroundSize: '42px 42px',
    pointerEvents: 'none',
  },
};

interface EmpresaComCategoria extends Empresa {
  categoria_nome?: string;
}

const ramoLabels: Record<string, string> = {
  comercio: 'Comércio',
  industrial: 'Industrial',
  prestacao_servico: 'Prestação de Serviço',
};

const formatRamoAtuacao = (value?: string) => value ? ramoLabels[value] || value : 'Não informado';
const formatCategoria = (empresa: EmpresaComCategoria) => empresa.categoria_nome || empresa.categoria_personalizada || 'Não informada';

interface IBGEEstado {
  id: number;
  sigla: string;
  nome: string;
}

interface IBGEMunicipio {
  id: number;
  nome: string;
}

const Busca: React.FC = () => {
  const [empresas, setEmpresas] = useState<EmpresaComCategoria[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalEmpresas, setTotalEmpresas] = useState(0);
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [totalPaginas, setTotalPaginas] = useState(1);

  const [categorias, setCategorias] = useState<Categoria[]>([]);
  const [subcategorias, setSubcategorias] = useState<Subcategoria[]>([]);
  const [filtrosExpandidos, setFiltrosExpandidos] = useState(false);

  // Estados e Cidades (IBGE)
  const [estados, setEstados] = useState<IBGEEstado[]>([]);
  const [cidades, setCidades] = useState<IBGEMunicipio[]>([]);
  const [cidadesLoading, setCidadesLoading] = useState(false);

  // Debounce ref para auto-search
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [filtros, setFiltros] = useState<EmpresaFilters>({
    nome: '',
    categoria: undefined,
    subcategorias: [],
    estado: '',
    cidade: '',
    pagina: 1,
    limite: 8,
  });

  // ── Carregar estados do IBGE ───────────────────────────────────────────────
  useEffect(() => {
    fetch('https://servicodados.ibge.gov.br/api/v1/localidades/estados?orderBy=nome')
      .then(r => r.json())
      .then((data: IBGEEstado[]) => setEstados(data))
      .catch(() => {});
  }, []);

  // ── Carregar cidades quando estado muda ───────────────────────────────────
  useEffect(() => {
    if (!filtros.estado) {
      setCidades([]);
      return;
    }
    setCidadesLoading(true);
    fetch(`https://servicodados.ibge.gov.br/api/v1/localidades/estados/${filtros.estado}/municipios?orderBy=nome`)
      .then(r => r.json())
      .then((data: IBGEMunicipio[]) => setCidades(data))
      .catch(() => setCidades([]))
      .finally(() => setCidadesLoading(false));
  }, [filtros.estado]);

  const handleCategoriaChange = (catId: number | '') => {
    setFiltros(prev => ({ ...prev, categoria: catId || undefined, subcategorias: [] }));
    setPaginaAtual(1);
  };

  const toggleSubcategoria = (id: number) => {
    setFiltros(prev => ({
      ...prev,
      subcategorias: (prev.subcategorias || []).includes(id)
        ? (prev.subcategorias || []).filter(s => s !== id)
        : [...(prev.subcategorias || []), id],
    }));
    setPaginaAtual(1);
  };

  const carregarCategorias = useCallback(async () => {
    try {
      const response = await empresaService.listarCategorias();
      if (response.success) {
        setCategorias(response.data);
      }
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
    }
  }, []);

  const buscarEmpresas = useCallback(async (filtrosParam?: EmpresaFilters, pagina?: number) => {
    setLoading(true);
    setError(null);
    try {
      const filtrosBusca = {
        ...(filtrosParam ?? filtros),
        pagina: pagina ?? paginaAtual,
      };
      const response = await empresaService.listarEmpresas(filtrosBusca);
      if (response.success) {
        setEmpresas(response.data);
        setTotalEmpresas(response.pagination.total);
        setTotalPaginas(response.pagination.paginas);
      } else {
        setError('Erro ao buscar empresas');
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'Erro ao buscar empresas');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Inicialização ─────────────────────────────────────────────────────────
  useEffect(() => {
    carregarCategorias();
    buscarEmpresas(filtros, 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Auto-search com debounce ao mudar filtros ─────────────────────────────
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      buscarEmpresas(filtros, paginaAtual);
    }, 450);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtros, paginaAtual]);

  const carregarSubcategorias = useCallback(async () => {
    if (!filtros.categoria) return;
    try {
      const response = await empresaService.listarSubcategorias(filtros.categoria);
      if (response.success) {
        setSubcategorias(response.data);
      }
    } catch (error) {
      console.error('Erro ao carregar subcategorias:', error);
    }
  }, [filtros.categoria]);

  useEffect(() => {
    if (filtros.categoria) {
      carregarSubcategorias();
    } else {
      setSubcategorias([]);
    }
  }, [filtros.categoria, carregarSubcategorias]);

  const handleFiltroChange = (campo: keyof EmpresaFilters, valor: any) => {
    setFiltros(prev => ({ ...prev, [campo]: valor }));
    setPaginaAtual(1);
  };

  // Ao trocar estado, limpar cidade automaticamente
  const handleEstadoChange = (sigla: string) => {
    setFiltros(prev => ({ ...prev, estado: sigla, cidade: '' }));
    setPaginaAtual(1);
  };

  const limparFiltros = () => {
    setFiltros({
      nome: '',
      categoria: undefined,
      subcategorias: [],
      estado: '',
      cidade: '',
      pagina: 1,
      limite: 12,
    });
    setPaginaAtual(1);
  };

  const filtrosAtivos = Object.entries(filtros).filter(([key, value]) => {
    if (key === 'pagina' || key === 'limite') return false;
    if (Array.isArray(value)) return value.length > 0;
    return value !== '' && value !== undefined;
  });

  return (
    <Box sx={{ backgroundColor: '#f4f6f8', minHeight: '100vh' }}>
      {/* Hero Section */}
      <Box
        sx={{
          ...darkGridSx,
          py: { xs: 6, md: 8 },
          borderBottom: '6px solid #c23535',
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box
            component="img"
            src="/logo-white.png?v=20260611"
            alt="IDECONECTA"
            sx={{
              display: 'block',
              width: { xs: 180, md: 240 },
              maxWidth: '70vw',
              height: 'auto',
              objectFit: 'contain',
              mx: 'auto',
              mb: 3,
              animation: `${floatIn} 700ms ease both`,
            }}
          />
          <Typography
            component="h1"
            sx={{
              fontSize: { xs: '2rem', md: '3rem' },
              fontWeight: 700,
              mb: 1,
              color: '#fff',
              textAlign: 'center',
              textShadow: '0 18px 42px rgba(0,0,0,0.42)',
              animation: `${floatIn} 800ms ease 80ms both`,
            }}
          >
            Encontre Empresas Parceiras
          </Typography>
          <Typography
            sx={{
              color: 'rgba(255,255,255,0.92)',
              mb: 4,
              fontSize: { xs: '1rem', md: '1.1rem' },
              textAlign: 'center',
              maxWidth: 760,
              mx: 'auto',
              animation: `${floatIn} 800ms ease 160ms both`,
            }}
          >
            Conecte-se com empresas validadas da comunidade IDEBRASIL e descubra oportunidades de negócio
          </Typography>

          {/* Barra de busca principal */}
          <Paper
            sx={{
              p: { xs: 2, md: 3 },
              borderRadius: 1,
              maxWidth: 980,
              mx: 'auto',
              boxShadow: '0 28px 80px rgba(0,0,0,0.28)',
              border: '1px solid rgba(255,255,255,0.18)',
              animation: `${floatIn} 800ms ease 240ms both`,
            }}
          >
            <Grid container spacing={2} alignItems="center" justifyContent="center">
              <Grid item xs={12} md={5}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Nome, serviço ou descrição..."
                  value={filtros.nome}
                  onChange={(e) => handleFiltroChange('nome', e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {loading ? <CircularProgress size={16} sx={{ color: '#C23535' }} /> : <Search sx={{ color: '#C23535' }} />}
                      </InputAdornment>
                    ),
                    sx: { borderRadius: 1 },
                  }}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Categoria</InputLabel>
                  <Select
                    value={filtros.categoria || ''}
                    onChange={(e) => handleCategoriaChange(e.target.value ? parseInt(e.target.value as string) : '')}
                    label="Categoria"
                    disabled={categorias.length === 0}
                    sx={{ borderRadius: 1 }}
                  >
                    <MenuItem value=""><em>Todas</em></MenuItem>
                    {categorias.map((cat) => (
                      <MenuItem key={cat.id} value={cat.id}>{cat.nome}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={2}>
                <Button
                  fullWidth
                  variant="outlined"
                  size="medium"
                  onClick={() => setFiltrosExpandidos(!filtrosExpandidos)}
                  sx={{
                    minHeight: 40,
                    borderRadius: 1,
                    borderColor: '#C23535',
                    color: '#C23535',
                    fontWeight: 700,
                    whiteSpace: 'nowrap',
                    '&:hover': { borderColor: '#A52A2A', bgcolor: '#fff3f3' },
                  }}
                >
                  {filtrosExpandidos ? 'Ocultar local' : 'Filtrar local'}
                </Button>
              </Grid>

              <Grid item xs={12} md={2}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {filtrosAtivos.length > 0 && (
                    <Button
                      variant="outlined"
                      onClick={limparFiltros}
                      startIcon={<Clear />}
                      fullWidth
                      sx={{ borderRadius: 1, borderColor: '#C23535', color: '#C23535', '&:hover': { borderColor: '#A52A2A', bgcolor: '#fff3f3' } }}
                    >
                      Limpar
                    </Button>
                  )}
                  {filtrosAtivos.length === 0 && (
                    <Button
                      variant="contained"
                      startIcon={<Search />}
                      fullWidth
                      disabled
                      sx={{ bgcolor: '#C23535', borderRadius: 1, fontWeight: 700, py: 1 }}
                    >
                      Busca automática ativa
                    </Button>
                  )}
                </Box>
              </Grid>
            </Grid>

            {/* Subcategorias */}
            {subcategorias.length > 0 && (
              <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mr: 1, fontWeight: 600 }}>
                  Subcategorias:
                </Typography>
                {subcategorias.map((sub) => (
                  <Chip
                    key={sub.id}
                    label={sub.nome}
                    size="small"
                    onClick={() => toggleSubcategoria(sub.id)}
                    sx={{
                      cursor: 'pointer',
                      ...(filtros.subcategorias || []).includes(sub.id)
                        ? { bgcolor: '#C23535', color: '#fff', '&:hover': { bgcolor: '#A52A2A' } }
                        : { borderColor: '#C23535', color: '#C23535' },
                    }}
                    variant={(filtros.subcategorias || []).includes(sub.id) ? 'filled' : 'outlined'}
                  />
                ))}
              </Box>
            )}

            {/* Filtros de localização — Estado (Select IBGE) + Cidade (Select IBGE) */}
            <Box
              sx={{
                mt: 2,
                display: filtrosExpandidos ? 'flex' : 'none',
                gap: 2,
                flexWrap: 'wrap',
                alignItems: 'flex-start',
                justifyContent: 'center',
              }}
            >
              {/* Select Estado */}
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Estado</InputLabel>
                <Select
                  value={filtros.estado || ''}
                  onChange={(e) => handleEstadoChange(e.target.value)}
                  label="Estado"
                  sx={{ borderRadius: 1 }}
                >
                  <MenuItem value=""><em>Todos</em></MenuItem>
                  {estados.map((uf) => (
                    <MenuItem key={uf.sigla} value={uf.sigla}>
                      {uf.sigla} — {uf.nome}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Select Cidade — habilitado somente após escolher estado */}
              <FormControl size="small" sx={{ minWidth: 220 }} disabled={!filtros.estado || cidadesLoading}>
                <InputLabel>
                  {cidadesLoading ? 'Carregando...' : 'Cidade'}
                </InputLabel>
                <Select
                  value={filtros.cidade || ''}
                  onChange={(e) => handleFiltroChange('cidade', e.target.value)}
                  label={cidadesLoading ? 'Carregando...' : 'Cidade'}
                  sx={{ borderRadius: 1 }}
                >
                  <MenuItem value=""><em>Todas</em></MenuItem>
                  {cidades.map((cidade) => (
                    <MenuItem key={cidade.id} value={cidade.nome}>
                      {cidade.nome}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Indicador de estado selecionado */}
              {filtros.estado && (
                <Chip
                  label={`${filtros.estado}${filtros.cidade ? ` / ${filtros.cidade}` : ''}`}
                  size="small"
                  icon={<LocationOn style={{ fontSize: 14 }} />}
                  onDelete={() => handleEstadoChange('')}
                  sx={{ bgcolor: '#fff3f3', color: '#C23535', border: '1px solid #C23535', alignSelf: 'center' }}
                />
              )}
            </Box>

          </Paper>
        </Container>
      </Box>

      {/* Resultados */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 5 } }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        <Paper
          elevation={0}
          sx={{
            mb: 3,
            p: 2,
            borderRadius: 1,
            border: '1px solid rgba(31,35,40,0.08)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#2C2C2C' }}>
            {loading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={18} sx={{ color: '#C23535' }} />
                <span>Buscando...</span>
              </Box>
            ) : (
              <>
                <Box component="span" sx={{ color: '#C23535' }}>{totalEmpresas}</Box>
                {` empresa${totalEmpresas !== 1 ? 's' : ''} encontrada${totalEmpresas !== 1 ? 's' : ''}`}
              </>
            )}
          </Typography>

          {filtrosAtivos.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {filtrosAtivos.map(([key, value]) => (
                <Chip
                  key={key}
                  label={`${key}: ${Array.isArray(value) ? value.length : value}`}
                  size="small"
                  onDelete={() => handleFiltroChange(key as keyof EmpresaFilters, Array.isArray(value) ? [] : '')}
                  sx={{ bgcolor: '#fff3f3', color: '#C23535', border: '1px solid #C23535' }}
                />
              ))}
            </Box>
          )}
          {totalPaginas > 1 && (
            <Pagination
              count={totalPaginas}
              page={paginaAtual}
              onChange={(_, page) => setPaginaAtual(page)}
              color="primary"
              size="small"
              sx={{
                '& .MuiPaginationItem-root.Mui-selected': {
                  bgcolor: '#C23535',
                  '&:hover': { bgcolor: '#A52A2A' },
                },
              }}
            />
          )}
        </Paper>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress sx={{ color: '#C23535' }} />
          </Box>
        ) : (
          <>
            <Grid container spacing={3}>
              {empresas.map((empresa) => {
                return (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={empresa.id}>
                    <Card
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        borderRadius: 1,
                        border: '1px solid rgba(31,35,40,0.08)',
                        boxShadow: '0 14px 34px rgba(31,35,40,0.08)',
                        position: 'relative',
                        overflow: 'hidden',
                        transition: 'transform 0.2s, box-shadow 0.2s, border-color 0.2s',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          left: 0,
                          top: 0,
                          width: '100%',
                          height: 4,
                          bgcolor: '#2f8f4e',
                          transform: 'scaleX(0.25)',
                          transformOrigin: 'left',
                          transition: 'transform 220ms ease',
                        },
                        '&:hover': {
                          transform: 'translateY(-7px)',
                          boxShadow: '0 24px 52px rgba(31,35,40,0.14)',
                          borderColor: 'rgba(47,143,78,0.42)',
                        },
                        '&:hover::before': {
                          transform: 'scaleX(1)',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          p: 2,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          borderBottom: '1px solid #f0f0f0',
                        }}
                      >
                        <Box
                          sx={{
                            width: { xs: 74, sm: 64 },
                            height: { xs: 74, sm: 64 },
                            flex: '0 0 auto',
                            bgcolor: '#fff',
                            border: '1px solid rgba(32,37,41,0.08)',
                            borderRadius: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            p: 0.75,
                            overflow: 'hidden',
                          }}
                        >
                          {empresa.logo_url ? (
                            <Box
                              component="img"
                              src={empresa.logo_url}
                              alt={`Logo ${empresa.nome_fantasia || empresa.razao_social}`}
                              sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
                            />
                          ) : (
                            <Business fontSize="small" sx={{ color: '#C23535' }} />
                          )}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="subtitle1" noWrap fontWeight={700} color="#2C2C2C">
                            {empresa.nome_fantasia || empresa.razao_social}
                          </Typography>
                        </Box>
                      </Box>

                      <CardContent sx={{ flex: 1, pt: 1.5, display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.5, flex: 1 }}>
                          {(empresa.descricao_servico || '').length > 90
                            ? `${(empresa.descricao_servico || '').substring(0, 90)}...`
                            : (empresa.descricao_servico || 'Sem descrição disponível')
                          }
                        </Typography>
                        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                          <Chip
                            size="small"
                            label={`Ramo de atuação: ${formatRamoAtuacao(empresa.ramo_atuacao)}`}
                            sx={{
                              bgcolor: 'rgba(194,53,53,0.08)',
                              color: '#C23535',
                              border: '1px solid rgba(194,53,53,0.22)',
                              fontWeight: 800,
                              maxWidth: '100%',
                              height: 'auto',
                              py: 0.45,
                              '& .MuiChip-label': {
                                display: 'block',
                                whiteSpace: 'normal',
                                lineHeight: 1.25,
                              },
                            }}
                          />
                          <Chip
                            size="small"
                            label={`Categoria: ${formatCategoria(empresa)}`}
                            sx={{
                              bgcolor: 'rgba(47,143,78,0.08)',
                              color: '#2f8f4e',
                              border: '1px solid rgba(47,143,78,0.24)',
                              fontWeight: 800,
                              maxWidth: '100%',
                              height: 'auto',
                              py: 0.45,
                              '& .MuiChip-label': {
                                display: 'block',
                                whiteSpace: 'normal',
                                lineHeight: 1.25,
                              },
                            }}
                          />
                        </Box>
                        <Button
                          fullWidth
                          variant="contained"
                          onClick={() => window.location.href = `/empresa/${empresa.id}`}
                          sx={{ bgcolor: '#C23535', '&:hover': { bgcolor: '#A52A2A' }, borderRadius: 1, fontWeight: 700 }}
                        >
                          Ver perfil da empresa
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>

            {empresas.length === 0 && !loading && (
              <Paper
                sx={{
                  p: 6,
                  textAlign: 'center',
                  borderRadius: 1,
                  border: '1px solid rgba(194,53,53,0.12)',
                  boxShadow: 'none',
                }}
              >
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    bgcolor: '#fff3f3',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2,
                  }}
                >
                  <Business sx={{ fontSize: 40, color: '#C23535' }} />
                </Box>
                <Typography variant="h6" fontWeight={700} color="#2C2C2C" gutterBottom>
                  Nenhuma empresa encontrada
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Tente ajustar os filtros ou{' '}
                  <Box component="a" href="/empresa/cadastro" sx={{ color: '#C23535', fontWeight: 600, textDecoration: 'none' }}>
                    cadastre sua empresa
                  </Box>
                  {' '}no IDEBRASIL.
                </Typography>
              </Paper>
            )}

            {totalPaginas > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
                <Pagination
                  count={totalPaginas}
                  page={paginaAtual}
                  onChange={(_, page) => setPaginaAtual(page)}
                  color="primary"
                  size="large"
                  sx={{
                    '& .MuiPaginationItem-root.Mui-selected': {
                      bgcolor: '#C23535',
                      '&:hover': { bgcolor: '#A52A2A' },
                    },
                  }}
                />
              </Box>
            )}
          </>
        )}
      </Container>
    </Box>
  );
};

export default Busca;
